import React, { useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { authService } from '@/services/auth'
import api, { getErrorMessage } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { DateOfBirthInput } from '@/components/ui/DateOfBirthInput'
import { PostcodeLookup } from '@/components/ui/PostcodeLookup'
import { Address } from '@/services/postcode'
import { getStoredReferralCode } from '@/utils/referralTracking'

const registerSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/\d/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    confirm_password: z.string(),
    title: z.string().min(1, 'Please select a title'),
    first_name: z.string().min(2, 'First name must be at least 2 characters'),
    last_name: z.string().min(2, 'Last name must be at least 2 characters'),
    gender: z.string().min(1, 'Please select a gender'),
    date_of_birth: z.string().min(1, 'Date of birth is required'),
    mobile_number: z
      .string()
      .min(1, 'Mobile number is required')
      .refine((v) => isValidPhoneNumber(v || ''), 'Please enter a valid mobile number (include your country)'),
    address_line1: z.string().min(3, 'Address is required'),
    address_line2: z.string().optional(),
    city: z.string().min(2, 'City is required'),
    county: z.string().optional(),
    postcode: z
      .string()
      .regex(/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i, 'Please enter a valid UK postcode'),
    country: z.string().default('United Kingdom'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

type RegisterFormData = z.infer<typeof registerSchema>

// Reusable labelled input
function Field({
  label,
  error,
  children,
  className = '',
}: {
  label: string
  error?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}

// Simple text/email/tel/password input with consistent styling
const TextInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { error?: string; suffix?: React.ReactNode }
>(({ type = 'text', error, className = '', suffix, ...props }, ref) => {
  return (
    <div className="relative">
      <input
        ref={ref}
        type={type}
        className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${
          error ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
        } ${suffix ? 'pr-10' : ''} ${className}`}
        {...props}
      />
      {suffix && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">{suffix}</div>
      )}
    </div>
  )
})
TextInput.displayName = 'TextInput'

export const Register: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState(1)
  const [useManualAddress, setUseManualAddress] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    watch,
    control,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { country: 'United Kingdom' },
  })


  // WhatsApp-OTP confirmation. Our SMS gateway only delivers to UK numbers, so a
  // non-UK mobile gets its code over WhatsApp — and if the customer has no WhatsApp
  // on that number, the code simply never arrives. That is not a footnote to read
  // past: it is a reason to stop and let them go back. The amber hint on the mobile
  // field was passive and got missed (ticket TKT-20260901-06D7F4, a +353 sign-up
  // whose code could not be delivered), so this ASKS before the account is created.
  const [waConfirm, setWaConfirm] = useState<{ warning: string; data: RegisterFormData } | null>(null)

  // `confirmed` is passed explicitly by the dialog's Yes button. Reading the
  // waConfirm state instead would loop for ever: the button clears it before
  // re-submitting, so the gate would simply re-arm and ask again.
  const onSubmit = async (data: RegisterFormData, confirmed = false) => {
    // The server owns the UK/non-UK decision (utils.validator.resolve_otp_channel);
    // asking it keeps the warning and the actual delivery channel from diverging.
    // If the check itself fails we fall back to the local test rather than block
    // a legitimate sign-up.
    if (!confirmed) {
      try {
        const { data: check } = await api.post('/auth/otp-channel-check', {
          mobile_number: data.mobile_number,
        })
        if (check?.requires_confirmation) {
          setWaConfirm({ warning: check.warning, data })
          return
        }
      } catch {
        const m = (data.mobile_number || '').replace(/\s/g, '')
        const isNonUk = m.length > 3 && !(m.startsWith('+44') || m.startsWith('447') || m.startsWith('07'))
        if (isNonUk) {
          setWaConfirm({
            warning:
              'Your verification code will be sent to you on WhatsApp, because this is not a UK ' +
              'number. You will need WhatsApp active on this number to receive it. Continue?',
            data,
          })
          return
        }
      }
    }

    setIsLoading(true)
    setError(null)

    // Token comes from the reCAPTCHA onChange handler (kept fresh; cleared on expiry).
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
    if (siteKey && !recaptchaToken) {
      setError('Please complete the "I\'m not a robot" verification below before creating your account.')
      setIsLoading(false)
      return
    }

    try {
      // Backend expects `password_confirm`, form uses `confirm_password` — remap here
      const { confirm_password, ...rest } = data
      // Carry the referral code captured from ?ref= on the landing page (cookie,
      // 90 days) so the referrer is credited at SIGN-UP, not only if this person
      // later places an order. Undefined when there is no code — never blocking.
      const referral_code = getStoredReferralCode() || undefined
      await authService.register({ ...rest, password_confirm: confirm_password, recaptcha_token: recaptchaToken ?? '', referral_code })
      navigate('/verify', { state: { email: data.email, step: 'mobile' } })
    } catch (err) {
      setError(getErrorMessage(err))
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  const nextStep = async () => {
    let fields: (keyof RegisterFormData)[] = []
    if (step === 1) fields = ['email', 'password', 'confirm_password']
    else if (step === 2) fields = ['title', 'first_name', 'last_name', 'gender', 'date_of_birth', 'mobile_number']
    const isValid = await trigger(fields)
    if (isValid) setStep(step + 1)
  }

  const handleAddressSelect = (address: Address) => {
    setValue('address_line1', address.line_1, { shouldValidate: true })
    setValue('address_line2', address.line_2 || '', { shouldValidate: true })
    setValue('city', address.post_town, { shouldValidate: true })
    setValue('county', address.county || '', { shouldValidate: true })
    setValue('postcode', address.postcode, { shouldValidate: true })
    setUseManualAddress(true)
  }

  const stepLabels = ['Account', 'Personal', 'Address']

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4">
      {/* WhatsApp-OTP confirmation. Blocks account creation until the customer says
          yes, so someone without WhatsApp on a non-UK number can go back and change
          it rather than wait for a code that cannot arrive. */}
      {waConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="wa-otp-title"
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none" aria-hidden="true">💬</span>
              <div>
                <h3 id="wa-otp-title" className="text-lg font-semibold text-gray-900">
                  Your code will come by WhatsApp
                </h3>
                <p className="mt-2 text-sm text-gray-600">{waConfirm.warning}</p>
                <p className="mt-2 text-sm text-gray-600">
                  Sending to <strong>{waConfirm.data.mobile_number}</strong>.
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  No WhatsApp on this number? Choose <strong>No</strong> and enter a UK mobile
                  instead — we will text your code.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWaConfirm(null)}>
                No, go back
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const pending = waConfirm.data
                  setWaConfirm(null)
                  onSubmit(pending, true)   // explicit: the customer has said yes
                }}
              >
                Yes, continue
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Create your account</h2>
          <p className="mt-1.5 text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center mb-6">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 transition-colors ${
                    s < step
                      ? 'bg-primary-600 text-white'
                      : s === step
                      ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? '✓' : s}
                </div>
                <span className={`text-xs mt-1 font-medium ${s === step ? 'text-primary-600' : 'text-gray-400'}`}>
                  {stepLabels[s - 1]}
                </span>
              </div>
              {s < 3 && (
                <div className={`h-0.5 w-16 sm:w-24 mx-1 mb-4 flex-shrink-0 transition-colors ${s < step ? 'bg-primary-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="p-5 sm:p-7">
          {error && (
            <Alert variant="error" message={error} onClose={() => setError(null)} className="mb-4" />
          )}

          <form onSubmit={handleSubmit((d) => onSubmit(d))} className="space-y-4">

            {/* ── Step 1: Account ── */}
            {step === 1 && (
              <>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Account Details</h3>

                <Field label="Email Address" error={errors.email?.message}>
                  <TextInput
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    error={errors.email?.message}
                    {...register('email')}
                  />
                </Field>

                <Field label="Password" error={errors.password?.message}>
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Min. 8 chars, number & special character"
                    error={errors.password?.message}
                    suffix={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                    {...register('password')}
                  />
                  <p className="mt-1 text-xs text-gray-500">Min. 8 characters, including a number and special character</p>
                </Field>

                <Field label="Confirm Password" error={errors.confirm_password?.message}>
                  <TextInput
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    error={errors.confirm_password?.message}
                    suffix={
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                    {...register('confirm_password')}
                  />
                </Field>
              </>
            )}

            {/* ── Step 2: Personal ── */}
            {step === 2 && (
              <>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Personal Information</h3>

                {/* Title + Gender */}
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Title"
                    options={[
                      { value: 'Mr', label: 'Mr' },
                      { value: 'Mrs', label: 'Mrs' },
                      { value: 'Miss', label: 'Miss' },
                      { value: 'Ms', label: 'Ms' },
                      { value: 'Dr', label: 'Dr' },
                    ]}
                    placeholder="Select"
                    error={errors.title?.message}
                    {...register('title')}
                  />
                  <Select
                    label="Gender"
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' },
                      { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                    ]}
                    placeholder="Select"
                    error={errors.gender?.message}
                    {...register('gender')}
                  />
                </div>

                {/* First + Last name */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name" error={errors.first_name?.message}>
                    <TextInput
                      autoComplete="given-name"
                      error={errors.first_name?.message}
                      {...register('first_name')}
                    />
                  </Field>
                  <Field label="Last Name" error={errors.last_name?.message}>
                    <TextInput
                      autoComplete="family-name"
                      error={errors.last_name?.message}
                      {...register('last_name')}
                    />
                  </Field>
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <DateOfBirthInput
                    value={watch('date_of_birth') || ''}
                    onChange={(date) => setValue('date_of_birth', date)}
                    error={errors.date_of_birth?.message}
                  />
                </div>

                {/* Mobile — constrained width */}
                <Field label="Mobile Number" error={errors.mobile_number?.message}>
                  <div className="max-w-xs">
                    <Controller
                      control={control}
                      name="mobile_number"
                      render={({ field }) => (
                        <PhoneInput
                          international
                          defaultCountry="GB"
                          countryCallingCodeEditable={false}
                          placeholder="Enter mobile number"
                          value={field.value}
                          onChange={(value) => field.onChange(value || '')}
                          onBlur={field.onBlur}
                          className={`phone-input ${errors.mobile_number?.message ? 'phone-input--error' : ''}`}
                        />
                      )}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Select your country and enter your mobile number. UK customers: pick 🇬🇧 +44 and enter your number without the leading 0.
                  </p>
                  {(() => {
                    const m = (watch('mobile_number') || '').replace(/\s/g, '')
                    const isNonUk = m.length > 3 && !(m.startsWith('+44') || m.startsWith('447') || m.startsWith('07'))
                    if (!isNonUk) return null
                    return (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        <p className="font-semibold mb-0.5">This looks like a non-UK number.</p>
                        <p>
                          Your verification code will be sent by <strong>WhatsApp</strong> (we can only send SMS to UK numbers).
                          If you’d prefer to receive it by SMS, please go back and enter a <strong>UK mobile number</strong>.
                        </p>
                      </div>
                    )
                  })()}
                </Field>
              </>
            )}

            {/* ── Step 3: Address ── */}
            {step === 3 && (
              <>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Address Details</h3>

                {!useManualAddress ? (
                  <>
                    <PostcodeLookup
                      onAddressSelect={handleAddressSelect}
                      initialPostcode={watch('postcode') || ''}
                      error={errors.postcode?.message}
                    />
                    <button
                      type="button"
                      onClick={() => setUseManualAddress(true)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Enter address manually
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setUseManualAddress(false)}
                      className="mb-3 text-sm text-primary-600 hover:text-primary-700"
                    >
                      ← Use postcode lookup
                    </button>

                    <Field label="Postcode" error={errors.postcode?.message}>
                      <div className="max-w-[160px]">
                        <TextInput
                          placeholder="SW1A 1AA"
                          error={errors.postcode?.message}
                          {...register('postcode')}
                        />
                      </div>
                    </Field>

                    <Field label="Address Line 1" error={errors.address_line1?.message}>
                      <TextInput error={errors.address_line1?.message} {...register('address_line1')} />
                    </Field>

                    <Field label="Address Line 2 (optional)" error={errors.address_line2?.message}>
                      <TextInput error={errors.address_line2?.message} {...register('address_line2')} />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                      <Field label="City" error={errors.city?.message}>
                        <TextInput error={errors.city?.message} {...register('city')} />
                      </Field>
                      <Field label="County (optional)" error={errors.county?.message}>
                        <TextInput error={errors.county?.message} {...register('county')} />
                      </Field>
                    </div>

                    <Field label="Country">
                      <TextInput disabled className="bg-gray-50 text-gray-500" {...register('country')} />
                    </Field>
                  </>
                )}

                {/* Invisible reCAPTCHA */}
                {import.meta.env.VITE_RECAPTCHA_SITE_KEY && (
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      size="normal"
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                      onChange={setRecaptchaToken}
                      onExpired={() => setRecaptchaToken(null)}
                      onErrored={() => setRecaptchaToken(null)}
                    />
                  </div>
                )}
              </>
            )}

            {/* Navigation buttons */}
            <div className={`flex pt-2 ${step > 1 ? 'justify-between' : 'justify-end'}`}>
              {step > 1 && (
                <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              {step < 3 ? (
                <Button type="button" onClick={nextStep}>
                  Continue
                </Button>
              ) : (
                <Button
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading || (!!import.meta.env.VITE_RECAPTCHA_SITE_KEY && !recaptchaToken)}
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              )}
            </div>
          </form>
        </Card>

        <p className="text-center text-xs text-gray-400 mt-4">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="underline hover:text-gray-600">Terms & Conditions</Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline hover:text-gray-600">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  )
}

export default Register
